import {
  getFullAssessmentData,
  getAssessmentMarkingExport
} from '../../../../services/data-entry/assessments.service';
import IconTooltipButton from '../../../../components/button/IconTooltipButton';

const ExportResponsesButton = ({ survey, surveyId, isExporting, setIsExporting }) => {
  const { type } = survey || {};

  const fetchAssessmentResponses = async (assessmentId) => {
    try {
      const [assessmentData, markingData] = await Promise.all([
        getFullAssessmentData(assessmentId),
        getAssessmentMarkingExport(assessmentId)
      ]);

      return {
        assessmentData: assessmentData?.data || {},
        markingData: markingData?.data || {}
      };
    } catch (error) {
      console.error('Error fetching assessment responses:', error);
      throw error;
    }
  };

  const formatDate = () => {
    return new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const formatTimeUTC = (isoDate) => {
    try {
      if (!isoDate) return '';
      const date = new Date(isoDate);

      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');

      return `${hours}:${minutes}`;
    } catch (error) {
      return '';
    }
  };

  const stripHtml = (html) => {
    if (!html) return '';
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  };

  const getQuestionText = (question) => {
    if (question.title) return stripHtml(question.title);
    if (question.content) return stripHtml(question.content);
    return `Question ${question.order}`;
  };

  const getOptionText = (question, optionValue) => {
    if (!question.options || !Array.isArray(question.options)) return optionValue;
    const option = question.options.find((opt) => String(opt.value) === String(optionValue));
    return option ? stripHtml(option.option) : String(optionValue);
  };

  const generateCSVContent = (assessmentData, markingData) => {
    const template = assessmentData?.template || {};
    const assessmentType = assessmentData?.type || type || '';
    const students = assessmentData?.students?.sort((a, b) => a.code.localeCompare(b.code)) || [];
    const records = markingData?.paginatedResults || [];

    if (!students || students.length === 0) {
      return 'Response ID,Message\n1,"No students available for this assessment"';
    }

    // Build a lookup map of marking records by student code
    const recordsByCode = {};
    records.forEach((record) => {
      recordsByCode[record.code] = record;
    });

    // Filter out groupreference questions (they're just content/instructions)
    const actualQuestions = (template.questions || []).filter((q) => q.type?.value !== 'groupreference');

    // Create headers
    const headers = [
      'User Code',
      'First Name',
      'Last Name',
      'Preferred Name',
      'User Email',
      'Form/Class',
      'House',
      'Year Levels',
      'Date Marked',
      'Comments',
      'Assessment Type'
    ];

    actualQuestions.forEach((question, index) => {
      const questionText = getQuestionText(question);
      const safeQuestionText = questionText.replace(/"/g, '""');
      headers.push(`Q${index + 1}: ${safeQuestionText}`);
    });

    const escapedHeaders = headers.map((h) => `"${h.replace(/"/g, '""')}"`);
    const csvRows = [escapedHeaders.join(',')];

    students.forEach((student) => {
      const record = recordsByCode[student.code];
      const userInfo = record?.userInfo || {};
      const userInfoData = userInfo?.data || {};

      const markedBy = record?.questions?.find((q) => q.markingDoneBy);
      const markedDate = markedBy?.markingDoneBy?.updatedAt;

      const row = [
        `"${student.code || ''}"`,
        `"${student.firstName || ''}"`,
        `"${student.lastName || ''}"`,
        `"${student.preferredName || ''}"`,
        `"${userInfo.email || ''}"`,
        `"${userInfoData.form || ''}"`,
        `"${userInfoData.house || ''}"`,
        `"${student?.yearlevel?.map((y) => y?.name).join(', ') || ''}"`,
        `"${markedDate ? new Date(markedDate).toLocaleDateString() : ''}"`,
        `"${(record?.comments || '').replace(/"/g, '""')}"`,
        `"${assessmentType}"`
      ];

      actualQuestions.forEach((question) => {
        const recordQuestion = record?.questions?.find((rq) => rq.questionId === question.id);
        let answerText = '';

        if (recordQuestion?.answer?.length > 0) {
          const answerArray = recordQuestion.answer;

          switch (question.type?.value) {
            case 'singlechoice':
              answerText = getOptionText(question, answerArray[0]);
              break;

            case 'multiplechoice': {
              const selectedOptions = answerArray
                .filter((val) => val !== undefined && val !== null && val !== '')
                .map((val) => getOptionText(question, val));
              answerText = selectedOptions.join(', ');
              break;
            }

            case 'shortanswer':
            case 'longanswer':
              answerText = answerArray.join(' ');

              break;
            case 'time':
              answerText = formatTimeUTC(answerArray?.[0]);
              break;

            case 'date':
              answerText = new Date(answerArray?.[0]).toLocaleDateString();
              break;

            default:
              answerText = answerArray
                .filter((val) => val !== undefined && val !== null && val !== '')
                .join(', ');
          }
        }

        if (typeof answerText === 'string') {
          answerText = answerText.replace(/"/g, '""');
        }
        row.push(`"${answerText}"`);
      });

      csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
  };

  const downloadCSV = (content, filename) => {
    const BOM = '\uFEFF'; // UTF-8 BOM for proper encoding
    const blob = new Blob([BOM + content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleExport = async () => {
    if (!surveyId) {
      console.error('Survey ID is missing');
      return;
    }

    setIsExporting(true);

    try {
      // Fetch assessment data with responses
      const { assessmentData, markingData } = await fetchAssessmentResponses(surveyId);

      // Generate CSV content
      const csvContent = generateCSVContent(assessmentData, markingData);

      // Create filename
      const currentDate = formatDate();
      const assessmentName = (assessmentData.name || assessmentData.template?.title || 'assessment')
        .replace(/[^a-z0-9\s]/gi, '') // Remove special characters except spaces
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .toLowerCase();
      const filename = `${assessmentName}_responses_${currentDate}.csv`;

      // Download the file
      downloadCSV(csvContent, filename);
    } catch (error) {
      console.error('Error exporting assessment responses:', error);
      // You might want to show a toast notification here
      alert('Error exporting assessment responses. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <IconTooltipButton
      id={`export-responses-${surveyId}`}
      disabled={isExporting}
      title={isExporting ? 'Exporting...' : 'Export Responses'}
      icon={'fas fa-download'}
      onClick={handleExport}
    />
  );
};

export default ExportResponsesButton;
